/**
 * The organisation read shared by every screen that formats money or interprets times.
 *
 * One query key, the same one the shell and organisation settings already use, so the
 * tenant is fetched once per workspace and every consumer reads the same answer. The
 * fallbacks cover the render or two before the answer arrives: they keep formatting from
 * crashing, and the real currency and timezone replace them as soon as the query lands.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";

export interface TenantSettings {
  id: string;
  displayName: string;
  timezone: string;
  locale: string;
  currency: string;
}

export function useTenant() {
  const auth = useAuth();
  const membership = auth.session?.selectedMembership;
  return useQuery({
    queryKey: ["tenant", membership?.tenantId],
    queryFn: () => api<TenantSettings>("/api/v1/tenant"),
    enabled: Boolean(membership),
    // The currency and timezone of an organisation change somewhere between never and once,
    // so a shared five-minute answer is fresher than the question deserves.
    staleTime: 5 * 60_000,
  });
}

/** The tenant base currency, or the ISO default for the moment before the tenant loads. */
export function useTenantCurrency(): string {
  const tenant = useTenant();
  return tenant.data?.currency || "USD";
}

/** The tenant timezone, or UTC for the moment before the tenant loads. */
export function useTenantTimezone(): string {
  const tenant = useTenant();
  return tenant.data?.timezone || "UTC";
}
